import type { ArcLeftOut } from '@/api/generated';

import { resourceTypeLabel } from './arcTypes';

/**
 * Preview's numbers and words (organization-arc, group 7; D17 to D19, D37).
 * The frame is as wide as a common phone's screen in CSS pixels, so the
 * sections and cards wrap as they will in the app.
 */
export const ARC_PREVIEW_WIDTH_PX = 390;

export const PREVIEW_LABEL = 'Preview';

export const PREVIEW_DESCRIPTION =
  'The ARC as the app will show it to everyone, with your unsaved changes. Nothing is saved.';

/** Cards and buttons open nothing in the portal (D19). */
export const CARDS_NOTE = 'Cards and buttons cannot be opened in Preview.';

export const LEFT_OUT_HEADING = 'Not shown in the app';

export const NOTHING_LEFT_OUT = 'Everything in this ARC shows in the app.';

export const PREVIEW_FAILED = 'The preview could not be built. Please try again.';

/** One thing Preview left out, and why, as a sentence (D17). */
export function leftOutWords(entry: ArcLeftOut): string {
  const section = entry.section_name;
  const what =
    entry.title ??
    `A ${resourceTypeLabel(entry.resource_type ?? '').toLowerCase() || 'resource'}`;
  switch (entry.reason) {
    case 'SECTION_HIDDEN':
      return `${section}: hidden, so the app does not show it.`;
    case 'SECTION_EMPTY':
      return `${section}: nothing to show, so the app leaves it out.`;
    case 'ITEM_NOT_PUBLISHED':
      return `${what} (${section}): not published, so the app does not show it.`;
    case 'ITEM_UNAVAILABLE':
      return `${what} (${section}): no longer available, so the app does not show it.`;
    case 'ITEM_RESTRICTED':
      return `${what} (${section}): only for supporters or members, so Preview, which shows what everyone sees, leaves it out.`;
  }
}

/** The app's short form of a count: "12.4K", "1M". */
const COMPACT = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

/** The header's supporter count as the wireframe writes it: "12.4K Supporters" (D25). */
export function supporterWords(count: number): string {
  return `${COMPACT.format(count)} ${count === 1 ? 'Supporter' : 'Supporters'}`;
}

/**
 * The introduction's HTML with every link, the button included, turned into
 * a `<span>` that keeps its text and class: Preview opens nothing (D19), so
 * nothing in the frame can be followed. The HTML is the backend's
 * allow-listed rendering (`app/services/rich_text.py`); nothing else is
 * changed.
 */
export function inertIntroHtml(html: string): string {
  const body = new DOMParser().parseFromString(html, 'text/html').body;
  for (const link of Array.from(body.querySelectorAll('a'))) {
    const span = body.ownerDocument.createElement('span');
    if (link.className) span.className = link.className;
    span.append(...Array.from(link.childNodes));
    link.replaceWith(span);
  }
  return body.innerHTML;
}

/** The header's buttons, as the app labels them. Every one is switched off in Preview (D19, D25). */
export const SUPPORT_LABEL = 'Support';
export const DONATE_LABEL = 'Donate';
export const VOLUNTEER_LABEL = 'Volunteer';

/**
 * A card of a type whose module has not registered with the ARC yet: the
 * app cannot open it, so Preview draws it muted (`docs/mobile-api-arc.md`,
 * section 4).
 */
export const UNAVAILABLE_CARD = 'Not available in the app yet';

/** Inside the frame when the app would show no section (B20). */
export const EMPTY_TITLE = 'Nothing to show yet';
export const EMPTY_HINT = 'Add published content to a visible section.';
